import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, BadgePercent } from 'lucide-react';
import { NAV_CATEGORIES } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { getJourneyId, trackEvent, trackPageLifecycle } from '../lib/customerEventTracker';

const Contact = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || process.env.VITE_API_BASE_URL || process.env.VITE_API_URL || '';
  const PUBLIC_API_KEY = process.env.REACT_APP_PUBLIC_API_KEY || process.env.VITE_PUBLIC_API_KEY || '';
  const API_TIMEOUT_MS = 15000;
  const [data, setData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'CONTACT_SUPPORT_FORM',
      form_name: 'ContactForm',
      open_event_name: 'contact_form_open',
    }),
    [],
  );

  const contactInfo = [
    { id: 'office', icon: MapPin, titleKey: 'contact.info_office', titleFb: 'Office', text: 'Flat No 302, 2nd floor, Pruthivi Apartment, Street No. 8, Habsiguda, Secunderabad - 500007' },
    { id: 'email', icon: Mail, titleKey: 'contact.info_email', titleFb: 'Email', text: 'contact@solvetax.com', href: 'mailto:contact@solvetax.com' },
    { id: 'phone', icon: Phone, titleKey: 'contact.info_phone', titleFb: 'Phone', text: '+91 93460 04430', href: 'tel:+919346004430' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.name || !/^[0-9]{10}$/.test(data.phone)) {
      trackEvent({
        event_name: 'contact_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ContactForm',
        funnel_name: 'CONTACT_SUPPORT_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('CONTACT_SUPPORT_FORM'),
        error_code: 'validation_error',
        error_message: 'Name or phone validation failed',
      });
      toast.error(t('contact.toast_name_phone', 'Please enter your name and a valid 10-digit phone number'));
      return;
    }
    const payload = {
      your_name: data.name.trim(),
      phone_number: data.phone.trim(),
      email_address: data.email.trim() || null,
      service_required: data.service || null,
      your_message: data.message.trim() || null,
    };

    setLoading(true);
    trackEvent({
      event_name: 'contact_submit_attempt',
      event_action: 'submit',
      event_status: 'attempt',
      event_category: 'form',
      form_name: 'ContactForm',
      funnel_name: 'CONTACT_SUPPORT_FORM',
      funnel_step_number: 2,
      funnel_step_name: 'submit',
      journey_id: getJourneyId('CONTACT_SUPPORT_FORM'),
      phone_number: payload.phone_number,
      service_code: payload.service_required,
    });
    try {
      if (API_BASE) {
        await axios.post(`${API_BASE}/api/v1/contact-support`, payload, {
          timeout: API_TIMEOUT_MS,
          headers: {
            'Content-Type': 'application/json',
            ...(PUBLIC_API_KEY ? { 'X-Public-Api-Key': PUBLIC_API_KEY } : {}),
          },
        });
      }
      trackEvent({
        event_name: 'contact_submit_success',
        event_action: 'submit',
        event_status: 'success',
        event_category: 'form',
        form_name: 'ContactForm',
        funnel_name: 'CONTACT_SUPPORT_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('CONTACT_SUPPORT_FORM', { reset: true }),
        phone_number: payload.phone_number,
        service_code: payload.service_required,
        api_name: '/api/v1/contact-support',
        api_status_code: 201,
      });
      toast.success(t('contact.toast_success', 'Our support team will contact you within 2 hours.'));
      setSuccess({ your_name: payload.your_name, phone_number: payload.phone_number });
      setData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = detail?.error?.message || (typeof detail === 'string' ? detail : null) || err?.message || 'Request failed';
      trackEvent({
        event_name: 'contact_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ContactForm',
        funnel_name: 'CONTACT_SUPPORT_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('CONTACT_SUPPORT_FORM'),
        phone_number: payload.phone_number,
        service_code: payload.service_required,
        api_name: '/api/v1/contact-support',
        api_status_code: status || null,
        error_code: status ? `api_${status}` : 'api_error',
        error_message: msg,
      });
      if (status === 401) toast.error(t('contact.toast_unauthorized', 'Unauthorized: missing or invalid public API key.'));
      else if (status === 429) toast.error(t('contact.toast_rate', 'Rate limit exceeded. Please try again in a moment.'));
      else if (status === 422) toast.error(t('contact.toast_validation', 'Validation: {msg}', { msg }));
      else toast.error(t('contact.toast_backend', 'Backend: {msg}', { msg }));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return <SuccessView payload={success} onAbout={() => navigate('/about')} />;
  }

  return (
    <div className="pt-28">
      <section className="py-14 hero-gradient">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <h1 className="ui-title-xl mb-3">
            {t('contact.hero_title', 'Contact')} <span className="text-[#14A098]">{t('contact.hero_accent', 'Us')}</span>
          </h1>
          <p className="ui-body">{t('contact.hero_sub', 'Get in touch with our expert team for all your tax and financial needs')}</p>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {contactInfo.map((c) => (
              <Card key={c.id} className="p-5 rounded-2xl border border-gray-100 flex gap-4 items-start card-lift">
                <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0"><c.icon className="w-5 h-5 text-[#14A098]" /></div>
                <div>
                  <div className="font-semibold text-[#0f2540]">{t(c.titleKey, c.titleFb)}</div>
                  {c.href ? <a href={c.href} className="text-sm text-gray-600 hover:text-[#14A098]">{c.text}</a> : <div className="text-sm text-gray-600">{c.text}</div>}
                </div>
              </Card>
            ))}
            <Card className="p-5 rounded-2xl bg-gradient-to-br from-[#0f2540] to-[#14A098] text-white">
              <div className="flex items-center gap-2 mb-2"><BadgePercent className="w-5 h-5" /><span className="font-semibold">{t('offer.title', 'Special Offer')}</span></div>
              <p className="text-sm text-teal-100">
                {t('offer.body_pre', 'Submit the form and get a flat')}{' '}
                <b className="text-white">{t('offer.discount', '10% discount')}</b>{' '}
                {t('offer.body_after', 'on your first service with SolveTax.')}
              </p>
            </Card>
          </div>

          <Card className="lg:col-span-3 p-6 md:p-8 rounded-2xl border border-gray-100 shadow-lg bg-white">
            <h2 className="text-2xl font-bold text-[#0f2540] mb-1">{t('contact.form_title', 'Contact Support')}</h2>
            <p className="text-sm text-gray-500 mb-6">{t('contact.form_sub', 'Fill out the form and our support team will contact you within 2 hours.')}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label className="text-sm mb-2 block">{t('contact.label_name', 'Your Name')} <span className="text-rose-500">*</span></Label><Input value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder={t('contact.ph_name', 'Full name')} /></div>
                <div><Label className="text-sm mb-2 block">{t('contact.label_phone', 'Phone Number')} <span className="text-rose-500">*</span></Label><Input value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder={t('contact.ph_phone', '10-digit mobile')} /></div>
                <div><Label className="text-sm mb-2 block">{t('contact.label_email', 'Email Address')}</Label><Input type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} placeholder={t('contact.ph_email', 'you@email.com')} /></div>
                <div><Label className="text-sm mb-2 block">{t('contact.label_service', 'Service Required')}</Label>
                  <Select value={data.service} onValueChange={(v) => setData({ ...data, service: v })}>
                    <SelectTrigger><SelectValue placeholder={t('contact.ph_service', 'Select a service')} /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {NAV_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.category} value={cat.label}>{t(`category.${cat.category}`, cat.label)}</SelectItem>
                      ))}
                      <SelectItem value="Other Services">{t('contact.other_services', 'Other Services')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-sm mb-2 block">{t('contact.label_message', 'Your Message')}</Label><Textarea rows={5} value={data.message} onChange={(e) => setData({ ...data, message: e.target.value })} placeholder={t('contact.ph_message', 'Tell us about your requirement')} /></div>
              <div className="flex flex-wrap gap-3 pt-2 items-center">
                <Button type="submit" disabled={loading} size="lg" className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full px-8 h-12 shadow-md min-w-[200px]">
                  {loading ? t('contact.btn_sending', 'Sending...') : (<>{t('form.submit_details', 'Submit Details')} <Send className="w-4 h-4 ml-2" /></>)}
                </Button>
                <Button type="button" variant="outline" onClick={() => setData({ name: '', email: '', phone: '', service: '', message: '' })} className="rounded-full h-12 border-gray-300">
                  {t('contact.btn_reset', 'Reset')}
                </Button>
                <p className="text-xs text-gray-500 mt-2 w-full">{t('contact.legal', 'By submitting, you agree to our Terms & Privacy Policy.')}</p>
              </div>
            </form>
          </Card>
        </div>
      </section>
    </div>
  );
};

const SuccessView = ({ payload, onAbout }) => {
  const { t } = useLanguage();
  useEffect(() => {
    const timer = setTimeout(() => onAbout(), 3000);
    return () => clearTimeout(timer);
  }, [onAbout]);

  const firstName = payload.your_name.split(' ')[0];

  return (
    <div className="pt-28 pb-16 hero-gradient min-h-screen">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <Card className="p-8 md:p-12 rounded-2xl border border-gray-100 shadow-xl bg-white text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] mb-3">{t('contact.success_title', 'Thank you, {name}!', { name: firstName })}</h1>
          <p className="text-gray-600 mb-6">{t('contact.success_body', 'Our support team will reach out at +91 {phone} within 2 hours.', { phone: payload.phone_number })}</p>
          <p className="text-sm text-gray-500">{t('contact.success_redirect', 'Redirecting to About page...')}</p>
        </Card>
      </div>
    </div>
  );
};

export default Contact;
